import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addProduct,
  createWatchList,
  deleteProduct,
  deleteWatchList,
  getProduct,
  getWatchLists,
  renameWatchlist,
} from "./data.thunk";

const INIT_STATE = {
  watchLists: [],
  products: {},
  loading: false,
  error: null,
};

export const fetchWatchLists = createAsyncThunk(
  "watchlist/fetchWatchlists",
  getWatchLists
);

export const createWatchListAsyncThunk = createAsyncThunk(
  "watchlist/createWatchList",
  createWatchList
);

export const deleteWatchListAsyncThunk = createAsyncThunk(
  "watchlist/deleteWatchList",
  deleteWatchList
);

export const addProductWatchlistAsyncThunk = createAsyncThunk(
  "watchlist/addProductWatchlist",
  addProduct
);

export const getProductAsyncThunk = createAsyncThunk(
  "watchlist/getProductWatchlist",
  getProduct
);

export const deleteProductAsyncThunk = createAsyncThunk(
  "watchlist/deleteProductWatchlist",
  deleteProduct
);

export const renameWatchlistAsyncThunk = createAsyncThunk(
  "watchlist/renameWatchlistWatchlist",
  renameWatchlist
);

export const getAllProductsIdsAsyncThunk = createAsyncThunk(
  "watchlist/getAllProductsIds",
  getProduct
);

const watchlistSlice = createSlice({
  name: "watchlist",
  initialState: INIT_STATE,
  reducers: {
    setWatchlistName: (state, action) => {
      state.watchLists[Number(action.payload.index)].watchlist_name =
        action.payload.value;
    },
    clearWatchlistData: (state) => {
      state.watchLists = [];
      state.products = {};
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch watchlists
      .addCase(fetchWatchLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWatchLists.fulfilled, (state, action) => {
        state.loading = false;
        action.payload.reverse();
        state.watchLists = action.payload;
      })
      .addCase(fetchWatchLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create watchlist
      .addCase(createWatchListAsyncThunk.pending, (state, action) => {
        const tempWatchlist = {
          id: `temp-${Date.now()}`,
          watchlist_name: action.meta.arg,
          isTemporary: true,
        };
        state.watchLists.push(tempWatchlist);
      })
      .addCase(createWatchListAsyncThunk.fulfilled, (state, action) => {
        state.watchLists = state.watchLists.map((watchlist) =>
          watchlist.isTemporary
            ? { ...action.payload, isTemporary: false } // Replace with the real watchlist
            : watchlist
        );
      })
      .addCase(createWatchListAsyncThunk.rejected, (state, action) => {
        state.watchLists = state.watchLists.filter(
          (watchlist) => !watchlist.isTemporary
        );
        state.error = action.payload;
      })
      // Delete watchlist
      .addCase(deleteWatchListAsyncThunk.pending, (state, action) => {
        const watchlistIdToDelete = action.meta.arg; // Fetch the ID passed to the thunk
        state.watchLists = state.watchLists.filter(
          (watchlist) => watchlist.watchlist_id !== watchlistIdToDelete
        );

        // Remove watchlist data from products object if present
        if (state.products[watchlistIdToDelete]) {
          delete state.products[watchlistIdToDelete];
        }
        state.loading = true;
      })
      .addCase(deleteWatchListAsyncThunk.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteWatchListAsyncThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add product to watchlist
      .addCase(addProductWatchlistAsyncThunk.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(addProductWatchlistAsyncThunk.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addProductWatchlistAsyncThunk.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      // Get all products.
      .addCase(getProductAsyncThunk.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getProductAsyncThunk.fulfilled, (state, action) => {
        state.loading = false;

        let watchlist = state.watchLists.find(
          (item, index) => item.watchlist_id == action.meta.arg.watchlist_id
        );
        watchlist.products = action.payload.data;
      })
      .addCase(getProductAsyncThunk.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      // Delete product from watchlist
      .addCase(deleteProductAsyncThunk.pending, (state, action) => {
        state.loading = true;
        // Remove product_id from products if present
        if (state.products[action.meta.arg.watchlist_id]) {
          state.products[action.meta.arg.watchlist_id] = state.products[
            action.meta.arg.watchlist_id
          ].filter((id) => id !== action.meta.arg.product_id);
        }
      })
      .addCase(deleteProductAsyncThunk.fulfilled, (state, action) => {
        let watchlist = state.watchLists.find(
          (item) => item.watchlist_id == action.meta.arg.watchlist_id
        );
        if (watchlist?.products) {
          // Remove the product from the watchlist's products array
          watchlist.products = watchlist.products.filter(
            (item) =>
              item.products_data[0].product_id !== action.meta.arg.product_id
          );

          // Also update the products state if it exists
          if (state.products[action.meta.arg.watchlist_id]) {
            state.products[action.meta.arg.watchlist_id] = state.products[
              action.meta.arg.watchlist_id
            ].filter((id) => id !== action.meta.arg.product_id);
          }
        }
        state.loading = false;
      })

      // Edit watchlist name
      .addCase(renameWatchlistAsyncThunk.fulfilled, (state, action) => {})

      .addCase(getAllProductsIdsAsyncThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllProductsIdsAsyncThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { watchlist_id } = action.meta.arg;
        const productIds = action.payload.data.map(
          (element) => element.products_data[0].product_id
        );

        if (state.products[watchlist_id]) {
          // If watchlist exists, add new products avoiding duplicates
          const existingIds = new Set(state.products[watchlist_id]);
          productIds.forEach((id) => existingIds.add(id));
          state.products[watchlist_id] = Array.from(existingIds);
        } else {
          // If watchlist doesn't exist, create new array
          state.products[watchlist_id] = productIds;
        }
      });
  },
});

export const { setWatchlistName, clearWatchlistData } = watchlistSlice.actions;
export default watchlistSlice.reducer;
